import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  useTheme,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import PositionCalculator from '../components/tools/PositionCalculator';
import PyramidCalculator from '../components/tools/PyramidCalculator';
import AverageCostCalculator from '../components/tools/AverageCostCalculator';
import FunnelCalculator from '../components/tools/FunnelCalculator';
import RectangleCalculator from '../components/tools/RectangleCalculator';
import TradingSettings from '../components/tools/TradingSettings';
import { useLocation } from 'react-router-dom';
import { ToolsSEO } from '../components/SEOPages';

// 定义工具面板组件
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tool-tabpanel-${index}`}
      aria-labelledby={`tool-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// 工具页面主组件
const Tools = () => {
  return (
    <>
      <ToolsSEO />
      <ToolsContent />
    </>
  );
};

const ToolsContent = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [showIntro, setShowIntro] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tradingSettings, setTradingSettings] = useState({
    market: 'A股',
    commissionRate: 0.03,
    stampDutyRate: 0.1,
    platformFee: 0,
    minFee: 0
  });
  const theme = useTheme();
  const location = useLocation();
  
  // 从localStorage加载交易设置
  useEffect(() => {
    const savedMarket = localStorage.getItem('tradingMarket');
    const savedCommissionRate = localStorage.getItem('tradingCommissionRate');
    const savedStampDutyRate = localStorage.getItem('tradingStampDutyRate');
    const savedPlatformFee = localStorage.getItem('tradingPlatformFee');
    const savedMinFee = localStorage.getItem('tradingMinFee');
    
    if (savedMarket || savedCommissionRate || savedStampDutyRate || savedPlatformFee || savedMinFee) {
      setTradingSettings({
        market: savedMarket || 'A股',
        commissionRate: savedCommissionRate ? parseFloat(savedCommissionRate) : 0.03,
        stampDutyRate: savedStampDutyRate ? parseFloat(savedStampDutyRate) : 0.1,
        platformFee: savedPlatformFee ? parseFloat(savedPlatformFee) : 0,
        minFee: savedMinFee ? parseFloat(savedMinFee) : 0
      });
    }
  }, []);

  // 处理URL中的hash，用于页面定位
  useEffect(() => {
    if (location.hash === '#position-intro') {
      setCurrentTab(0); // 确保选中仓位管理计算器标签
      setShowIntro(true); // 显示介绍部分
      
      // 等待DOM更新后滚动到指定位置
      setTimeout(() => {
        const element = document.getElementById('position-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else if (location.hash === '#pyramid-intro') {
      setCurrentTab(1); // 确保选中金字塔计算器标签
      setShowIntro(true); // 显示介绍部分
      
      // 等待DOM更新后滚动到指定位置
      setTimeout(() => {
        const element = document.getElementById('pyramid-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else if (location.hash === '#funnel-intro') {
      setCurrentTab(2); // 确保选中漏斗型计算器标签
      setShowIntro(true); // 显示介绍部分
      
      // 等待DOM更新后滚动到指定位置
      setTimeout(() => {
        const element = document.getElementById('funnel-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else if (location.hash === '#rectangle-intro') {
      setCurrentTab(3); // 确保选中矩形计算器标签
      setShowIntro(true); // 显示介绍部分
      
      // 等待DOM更新后滚动到指定位置
      setTimeout(() => {
        const element = document.getElementById('rectangle-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else if (location.hash === '#average-cost-intro') {
      setCurrentTab(4); // 确保选中加仓成本计算器标签
      setShowIntro(true); // 显示介绍部分
      
      // 等待DOM更新后滚动到指定位置
      setTimeout(() => {
        const element = document.getElementById('average-cost-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleSettingsSave = (settings) => {
    setTradingSettings(settings);
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          交易小工具
        </Typography>
        <Tooltip title="费率设置">
          <IconButton 
            onClick={handleSettingsOpen}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              }
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label="trading tools tabs"
        >
          <Tab label="仓位管理计算器" />
          <Tab label="金字塔仓位计算器" />
          {/* <Tab label="漏斗型仓位计算器" /> */}
          {/* <Tab label="矩形仓位计算器" /> */}
          <Tab label="加仓成本计算器" />
          {/* 未来可以在这里添加更多工具的标签页 */}
        </Tabs>
        <Divider />
        
        <TabPanel value={currentTab} index={0}>
          <PositionCalculator showIntroFromParent={showIntro} tradingSettings={tradingSettings} />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <PyramidCalculator showIntroFromParent={showIntro} tradingSettings={tradingSettings} />
        </TabPanel>
        {/* <TabPanel value={currentTab} index={2}>
          <FunnelCalculator showIntroFromParent={showIntro} tradingSettings={tradingSettings} />
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          <RectangleCalculator showIntroFromParent={showIntro} tradingSettings={tradingSettings} />
        </TabPanel> */}
        <TabPanel value={currentTab} index={2}>
          <AverageCostCalculator tradingSettings={tradingSettings} />
        </TabPanel>
        {/* 未来可以在这里添加更多工具的内容面板 */}
      </Paper>
      
      {/* 交易设置对话框 */}
      <TradingSettings 
        open={settingsOpen}
        onClose={handleSettingsClose}
        onSave={handleSettingsSave}
      />
    </Container>
  );
};

export default Tools;